import type { CSSProperties, ReactNode } from 'react';
import { Card, Divider, Typography } from 'antd';
import { TbSquareRounded } from 'react-icons/tb';
import type { TooltipPayloadEntry, TooltipValueType } from 'recharts';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { dayjs } from '@/utilities';

export const antDesignBoxShadowBottom = 'inset 0 -10px 8px -8px rgba(0, 0, 0, 0.15)';

interface CustomTooltipProps {
    active?: boolean;
    label?: ReactNode;
    labelFormatter?: (
        label: ReactNode,
        payload: ReadonlyArray<TooltipPayloadEntry<TooltipValueType, number | string>>,
    ) => ReactNode;
    nameFormatter?: (name: unknown) => ReactNode;
    payload?: ReadonlyArray<TooltipPayloadEntry<TooltipValueType, number | string>>;
    resourceColorMap?: Record<string, string>;
    resourceFillMap?: Record<string, string>;
    resourceLabelMap?: Record<string, string>;
    valueFormatter?: (value: unknown, name: unknown) => ReactNode;
}

export interface ChartTooltipRow {
    color?: string;
    fill?: string;
    key: string;
    label: ReactNode;
    value: ReactNode;
}

export interface ChartTooltipContentProps {
    compact?: boolean;
    label?: ReactNode;
    maxHeight?: CSSProperties['maxHeight'];
    rows: ChartTooltipRow[];
}

export const ChartTooltipContent = ({
    compact = false,
    label,
    maxHeight = 'min(20rem, calc(100vh - 2rem))',
    rows,
}: ChartTooltipContentProps) => {
    const { token } = useAntDesignTheme();

    return (
        <Card
            styles={{
                body: {
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight,
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left',
                },
            }}
            style={{
                background: token.colorBgElevated,
                borderColor: token.colorBorderSecondary,
                borderRadius: token.borderRadius,
                maxWidth: compact ? 'calc(100vw - 1rem)' : undefined,
                minWidth: compact ? '9rem' : '13rem',
                overflow: 'hidden',
                width: compact ? 'max-content' : undefined,
            }}
        >
            {label ? (
                <div
                    style={{
                        background: token.colorBgContainer,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        padding: '0.5rem 0.75rem',
                    }}
                >
                    <Typography.Text style={{ color: token.colorTextSecondary, whiteSpace: 'nowrap' }}>
                        <small>{label}</small>
                    </Typography.Text>
                </div>
            ) : null}
            <div
                data-chart-tooltip-scroll-region='true'
                style={{
                    minHeight: 0,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    padding: '0.5rem 0.75rem',
                    scrollbarGutter: 'stable',
                }}
            >
                {rows.map((row, index) => (
                    <div key={row.key}>
                        {index ? <Divider style={{ margin: '0.375rem 0' }} /> : null}
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '0.75rem',
                                justifyContent: 'space-between',
                                minWidth: 0,
                            }}
                        >
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <TbSquareRounded
                                    fill={row.fill || token.cyan1}
                                    size='1rem'
                                    stroke={row.color || row.fill || token.cyan4}
                                    style={{
                                        flex: 'none',
                                        marginRight: '0.375rem',
                                    }}
                                />
                                <small>{row.label}</small>
                            </span>
                            <small
                                style={{
                                    color: token.colorTextSecondary,
                                    flex: 'none',
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {row.value}
                            </small>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

const payloadFill = (payload: unknown): string | undefined => {
    if (!payload || typeof payload !== 'object' || !('fill' in payload)) {
        return undefined;
    }

    return typeof payload.fill === 'string' ? payload.fill : undefined;
};

const payloadStroke = (payload: unknown): string | undefined => {
    if (!payload || typeof payload !== 'object' || !('stroke' in payload)) {
        return undefined;
    }

    return typeof payload.stroke === 'string' ? payload.stroke : undefined;
};

const CustomRechartsUsageAnalyticsTooltip = ({
    active,
    payload,
    label,
    labelFormatter,
    nameFormatter,
    resourceColorMap = {},
    resourceFillMap = {},
    resourceLabelMap = {},
    valueFormatter,
}: CustomTooltipProps) => {
    const { token } = useAntDesignTheme();

    if (active && payload && payload.length) {
        const dateLabel = typeof label === 'string' || typeof label === 'number' ? label : undefined;
        const formattedLabel = labelFormatter
            ? labelFormatter(label, payload)
            : dateLabel !== undefined && dayjs(dateLabel).isValid()
              ? dayjs(dateLabel).format('MMM Do, YYYY')
              : label;

        return (
            <ChartTooltipContent
                label={formattedLabel}
                rows={payload.map((payloadEntry, index) => {
                    const key = String(payloadEntry.dataKey ?? payloadEntry.name ?? index);
                    const fill = payloadFill(payloadEntry.payload);
                    const stroke = payloadStroke(payloadEntry.payload);
                    const name = payloadEntry.name ?? payloadEntry.dataKey;

                    return {
                        color: resourceColorMap[key] || stroke || payloadEntry.color || fill || token.cyan4,
                        fill: resourceFillMap[key] || fill || token.cyan1,
                        key: `${key}-${index}`,
                        label: nameFormatter ? nameFormatter(name) : (resourceLabelMap[key] ?? name ?? key),
                        value: valueFormatter
                            ? valueFormatter(payloadEntry.value, name)
                            : String(payloadEntry.value ?? ''),
                    };
                })}
            />
        );
    }

    return null;
};

export default CustomRechartsUsageAnalyticsTooltip;
